<template>
    <section class="esesja-modal">
        <section class="esesja-modal__wraper">
            <section class="esesja-modal__wraper__header">
                <h3 class="esesja-modal__wraper__header__title">
                    {{ fieldLabel }} - {{$t('com.file.header-user-info.versions-history')}}
                </h3>
                <button class="esesja-modal__wraper__header__exit-btn" type="button" @click="close">
                    <img src="@/assets/icons/components/exit-btn.svg" />
                </button>
            </section>
            <section class="esesja-modal__wraper__body">
                <section class="contact-history__current">
                    <span class="contact-history__current-label">{{ fieldLabel }}:</span>
                    <span class="contact-history__current-value">{{ currentValueDisplay }}</span>
                </section>

                <section class="contact-history__list" v-if="history.length > 0">
                    <section v-for="item in history" :key="item.id" class="contact-history__item" :class="{ 'contact-history__item--current': item.is_current }">
                        <section class="contact-history__item-top">
                            <span class="contact-history__value">{{ item.field_value }}</span>
                            <button class="contact-history__delete-btn" type="button" @click="deleteHistory(item)">
                                <img src="@/assets/icons/components/delete.svg" />
                            </button>
                        </section>
                        <span class="contact-history__dates">
                            {{ formatDate(item.valid_from) }}
                            <span v-if="item.valid_to"> - {{ formatDate(item.valid_to) }}</span>
                        </span>
                    </section>
                </section>
                <section class="contact-history__empty" v-else>
                    {{$t('com.file.versions-history.no-data')}}
                </section>

                <section class="esesja-modal__row">
                    <label class="esesja-modal__wraper__body__label">{{ $t('com.global.add') }} {{ fieldLabel }}</label>
                    <input class="esesja-modal__wraper__body__input" v-model="newValue" type="text" />
                </section>
                <section class="esesja-modal__row-double">
                    <section class="esesja-modal__col-double">
                        <label class="esesja-modal__wraper__body__label">{{$t('com.global.from')}}</label>
                        <section class="esesja-modal__picker-wrapper">
                            <input type="date" class="esesja-modal__wraper__body__picker" v-model="newValidFrom" :max="maxDate" @blur="markTouched('from')" />
                        </section>
                        <ErrorMessage v-if="touchedFrom && isError('valid_from', this.errors)"
                                      :errorMsgs="passErrorMsg('valid_from', this.errors)" />
                    </section>
                    <section class="esesja-modal__col-double">
                        <label class="esesja-modal__wraper__body__label">{{$t('com.global.to')}}</label>
                        <section class="esesja-modal__picker-wrapper">
                            <input type="date" class="esesja-modal__wraper__body__picker" v-model="newValidTo" :max="maxDate" @blur="markTouched('to')" />
                        </section>
                        <ErrorMessage v-if="touchedTo && isError('valid_to', this.errors)"
                                      :errorMsgs="passErrorMsg('valid_to', this.errors)" />
                    </section>
                </section>
                <ErrorMessage v-if="touchedFrom && touchedTo && isError('date_range', this.errors)"
                              :errorMsgs="passErrorMsg('date_range', this.errors)" />
                <ErrorMessage v-if="touchedFrom && touchedTo && isError('date_overlap', this.errors)"
                              :errorMsgs="passErrorMsg('date_overlap', this.errors)" />

                <section class="esesja-modal__wraper__body__bottom">
                    <button class="esesja-modal__wraper__body__bottom-button" type="button" @click="close">{{$t('com.global.cancel')}}</button>
                    <button class="esesja-modal__wraper__body__bottom-button esesja-modal__wraper__body__bottom-button--red" type="button" @click="addHistory" :disabled="isSaving || newValue.length === 0 || isInvalidDateRange || isMissingDates || isOverlappingRange">
                        {{$t('com.global.save')}}
                    </button>
                </section>
            </section>
        </section>
        <LoaderInside v-if="isLoading" />
    </section>
</template>

<script charset="utf-8">
    import moment from "moment";
    import LoaderInside from "@/components/BasicControls/LoaderInside.vue";
    import ErrorMessage from "@/components/BasicControls/ErrorMessage.vue";
    import ContactService from "@/services/contact.service";
    import { passErrorMsg, isError, setErrors } from "@/formValidation/main";

    export default {
        name: "ContactFieldHistoryModal",
        components: {
            LoaderInside,
            ErrorMessage
        },
        props: {
            contactId: Number,
            fieldKey: String,
            fieldLabel: String,
            currentValue: String,
        },
        data() {
            return {
                history: [],
                newValue: "",
                newValidFrom: "",
                newValidTo: "",
                isLoading: false,
                isSaving: false,
                errors: [],
                touchedFrom: false,
                touchedTo: false,
            };
        },
        computed: {
            currentValueDisplay() {
                return this.currentValue && this.currentValue.length > 0 ? this.currentValue : "-";
            },
            maxDate() {
                return moment().format("YYYY-MM-DD");
            },
            isInvalidDateRange() {
                if (!this.newValidFrom || !this.newValidTo)
                    return false;
                return this.newValidFrom > this.newValidTo;
            },
            isMissingDates() {
                return !this.newValidFrom || !this.newValidTo;
            },
            isOverlappingRange() {
                if (this.isMissingDates || this.isInvalidDateRange)
                    return false;
                return this.history.some((item) => this.rangesOverlap(this.newValidFrom, this.newValidTo, item.valid_from, item.valid_to));
            }
        },
        watch: {
            fieldKey() {
                this.loadHistory();
            },
            newValidFrom() {
                this.updateValidationErrors();
            },
            newValidTo() {
                this.updateValidationErrors();
            },
            history() {
                this.updateValidationErrors();
            }
        },
        mounted() {
            this.loadHistory();
        },
        methods: {
            async loadHistory() {
                if (!this.contactId || !this.fieldKey) {
                    this.history = [];
                    return;
                }

                try {
                    this.isLoading = true;
                    const response = await ContactService.getContactAttributeHistory(this.contactId, this.fieldKey);
                    this.history = response.data ?? [];
                }
                catch (e) {
                    if (e.response && e.response.status == 401)
                        this.$router.push({ name: 'Login' })
                }
                finally {
                    this.isLoading = false;
                }
            },
            async addHistory() {
                if (!this.contactId || !this.fieldKey || this.newValue.length === 0)
                    return;

                this.touchedFrom = true;
                this.touchedTo = true;
                this.updateValidationErrors();
                if (this.errors.length > 0)
                    return;

                try {
                    this.isSaving = true;
                    await ContactService.addContactAttributeHistory(this.contactId, {
                        field_name: this.fieldKey,
                        field_value: this.newValue,
                        valid_from: this.newValidFrom && this.newValidFrom.length > 0 ? this.newValidFrom : null,
                        valid_to: this.newValidTo && this.newValidTo.length > 0 ? this.newValidTo : null,
                    });
                    this.newValue = "";
                    this.newValidFrom = "";
                    this.newValidTo = "";
                    this.errors = [];
                    this.touchedFrom = false;
                    this.touchedTo = false;
                    await this.loadHistory();
                }
                catch (e) {
                    if (e.response && e.response.status == 401)
                        this.$router.push({ name: 'Login' })
                }
                finally {
                    this.isSaving = false;
                }
            },
            async deleteHistory(item) {
                if (!this.contactId || !item?.id)
                    return;

                try {
                    this.isLoading = true;
                    await ContactService.deleteContactAttributeHistory(this.contactId, item.id);
                    await this.loadHistory();
                }
                catch (e) {
                    if (e.response && e.response.status == 401)
                        this.$router.push({ name: 'Login' })
                }
                finally {
                    this.isLoading = false;
                }
            },
            formatDate(date) {
                if (!date)
                    return "-";
                return moment(date).format("YYYY-MM-DD");
            },
            rangesOverlap(startA, endA, startB, endB) {
                const aStart = moment(startA, "YYYY-MM-DD").startOf("day");
                const aEnd = moment(endA, "YYYY-MM-DD").endOf("day");
                const bStart = moment(startB).startOf("day");
                const bEnd = endB ? moment(endB).endOf("day") : moment("9999-12-31", "YYYY-MM-DD").endOf("day");

                return aStart.isSameOrBefore(bEnd) && aEnd.isSameOrAfter(bStart);
            },
            markTouched(field) {
                if (field === "from")
                    this.touchedFrom = true;
                if (field === "to")
                    this.touchedTo = true;
            },
            updateValidationErrors() {
                const errors = {};

                if (this.isMissingDates) {
                    if (!this.newValidFrom)
                        errors.valid_from = [this.$t('com.global.required-field')];
                    if (!this.newValidTo)
                        errors.valid_to = [this.$t('com.global.required-field')];
                }

                if ((this.newValidFrom && this.newValidFrom > this.maxDate) || (this.newValidTo && this.newValidTo > this.maxDate)) {
                    errors.date_range = [this.$t('com.global.date-is-from-future')];
                }

                if (this.isInvalidDateRange) {
                    errors.date_range = [this.$t('com.global.valid-from-cannot-be-after-valid-to')];
                }

                if (this.isOverlappingRange) {
                    errors.date_overlap = [this.$t('com.global.date-range-overlaps-existing-period')];
                }

                this.errors = setErrors(errors);
            },
            isError,
            setErrors,
            passErrorMsg,
            close() {
                this.$emit("close");
            }
        },
        passErrorMsg,
        setErrors,
        isError,
    };
</script>

<style lang="scss" scoped>
    @use '@/assets/scss/esesja-modal.scss';

    .contact-history {
        &__current {
            display: flex;
            flex-direction: row;
            flex-wrap: wrap;
            column-gap: 8px;
            margin-bottom: 16px;
            font-size: 14px;
            color: #00082c;
        }

        &__current-label {
            font-weight: 600;
        }

        &__list {
            display: flex;
            flex-direction: column;
            row-gap: 10px;
            margin-bottom: 24px;
        }

        &__item {
            display: flex;
            flex-direction: column;
            padding: 10px 12px;
            border: 1px solid #d1d6df;
            border-radius: 6px;
            background: #ffffff;
        }

        &__item--current {
            border-color: #00082c;
            background: #f4f8ff;
        }

        &__value {
            font-weight: 600;
            color: #00082c;
            margin-bottom: 4px;
        }

        &__item-top {
            display: flex;
            align-items: center;
            justify-content: space-between;
            column-gap: 12px;
        }

        &__delete-btn {
            border: 1px solid #d1d6df;
            background: #ffffff;
            border-radius: 4px;
            width: 32px;
            height: 32px;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            transition: background 0.2s ease-in-out;

            &:hover {
                background: #e8ebef;
            }

            img {
                width: 14px;
                height: 14px;
            }
        }

        &__dates {
            font-size: 12px;
            color: #60667a;
        }

        &__empty {
            font-size: 14px;
            color: #60667a;
            margin-bottom: 24px;
        }
    }
</style>
